import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, X, Trash2, Library, Copy, ExternalLink, Save, GripVertical, Eye, EyeOff,
  ImagePlus, Image as ImageIcon,
} from "lucide-react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const imageUrl = (path) => (path ? `${API_BASE}/public/testes/image/${path}` : null);

const QUESTION_TYPES = [
  { value: "yes_no", label: "Sim/Não" },
  { value: "scale5", label: "Escala 1–5" },
];

const BAND_COLORS = [
  { value: "green", label: "Verde", cls: "bg-emerald-500" },
  { value: "amber", label: "Amarelo", cls: "bg-amber-500" },
  { value: "red",   label: "Vermelho", cls: "bg-rose-500" },
  { value: "muted", label: "Cinza", cls: "bg-muted-foreground" },
];

const emptyDraft = () => ({
  name: "",
  description: "",
  slug: "",
  conclusion_text: "",
  active: true,
  questions: [{ text: "", type: "yes_no", weight: 1, image_path: null }],
  score_bands: [
    { min: 0, max: 5, color: "green", label: "Baixo" },
    { min: 6, max: 10, color: "amber", label: "Moderado" },
    { min: 11, max: 999, color: "red", label: "Alto" },
  ],
});

function EditorModal({ test, onClose, onSaved }) {
  const isNew = !test;
  const [draft, setDraft] = useState(isNew ? emptyDraft() : {
    name: test.name || "",
    description: test.description || "",
    slug: test.slug || "",
    conclusion_text: test.conclusion_text || "",
    active: test.active !== false,
    questions: (test.questions || []).map((q) => ({
      id: q.id, text: q.text, type: q.type, weight: q.weight, image_path: q.image_path || null,
    })),
    score_bands: (test.score_bands || []).map((b) => ({ min: b.min, max: b.max, color: b.color, label: b.label })),
  });
  const [saving, setSaving] = useState(false);
  const [uploadingQid, setUploadingQid] = useState(null);
  const testId = test?.id || null;

  const setField = (key, val) => setDraft((d) => ({ ...d, [key]: val }));
  const setQ = (idx, key, val) => setDraft((d) => {
    const arr = [...d.questions];
    arr[idx] = { ...arr[idx], [key]: val };
    return { ...d, questions: arr };
  });
  const addQ = () => setDraft((d) => ({ ...d, questions: [...d.questions, { text: "", type: "yes_no", weight: 1, image_path: null }] }));
  const removeQ = (idx) => setDraft((d) => ({ ...d, questions: d.questions.filter((_, i) => i !== idx) }));
  const moveQ = (idx, dir) => setDraft((d) => {
    const arr = [...d.questions];
    const to = idx + dir;
    if (to < 0 || to >= arr.length) return d;
    [arr[idx], arr[to]] = [arr[to], arr[idx]];
    return { ...d, questions: arr };
  });

  const setBand = (idx, key, val) => setDraft((d) => {
    const arr = [...d.score_bands];
    arr[idx] = { ...arr[idx], [key]: val };
    return { ...d, score_bands: arr };
  });
  const addBand = () => setDraft((d) => ({ ...d, score_bands: [...d.score_bands, { min: 0, max: 0, color: "muted", label: "" }] }));
  const removeBand = (idx) => setDraft((d) => ({ ...d, score_bands: d.score_bands.filter((_, i) => i !== idx) }));

  const validate = () => {
    if (!draft.name.trim()) return "Informe o nome do teste.";
    if (!draft.questions.length) return "Adicione ao menos uma pergunta.";
    for (const q of draft.questions) {
      if (!q.text.trim()) return "Todas as perguntas precisam ter enunciado.";
      if (Number(q.weight) < 0) return "Pesos devem ser positivos.";
    }
    if (!draft.score_bands.length) return "Adicione ao menos uma faixa de score.";
    for (const b of draft.score_bands) {
      if (Number(b.max) < Number(b.min)) return "Em cada faixa, o max não pode ser menor que o min.";
    }
    return null;
  };

  const save = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      const payload = {
        name: draft.name.trim(),
        description: draft.description.trim(),
        slug: (draft.slug || "").trim() || undefined,
        conclusion_text: (draft.conclusion_text || ""),
        active: !!draft.active,
        questions: draft.questions.map((q) => ({
          id: q.id || undefined,
          text: q.text.trim(),
          type: q.type,
          weight: Number(q.weight) || 0,
          image_path: q.image_path || null,
        })),
        score_bands: draft.score_bands.map((b) => ({
          min: Number(b.min) || 0,
          max: Number(b.max) || 0,
          color: b.color || "muted",
          label: (b.label || "").trim(),
        })),
      };
      if (isNew) {
        await api.post("/admin/testes-catalogo", payload);
        toast.success("Teste criado.");
      } else {
        await api.patch(`/admin/testes-catalogo/${test.id}`, payload);
        toast.success("Teste atualizado.");
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar.");
    }
    setSaving(false);
  };

  const uploadImage = async (qid, file) => {
    if (!testId) {
      toast.error("Salve o teste primeiro para poder anexar imagens às perguntas.");
      return;
    }
    if (!qid) {
      toast.error("Salve o teste primeiro para gerar o ID da pergunta.");
      return;
    }
    setUploadingQid(qid);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(
        `/admin/testes-catalogo/${testId}/questions/${qid}/image`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setDraft((d) => ({
        ...d,
        questions: d.questions.map((q) => (q.id === qid ? { ...q, image_path: data.image_path } : q)),
      }));
      toast.success("Imagem anexada.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha no upload.");
    }
    setUploadingQid(null);
  };

  const removeImage = async (qid) => {
    if (!testId || !qid) return;
    if (!window.confirm("Remover a imagem desta pergunta?")) return;
    try {
      await api.delete(`/admin/testes-catalogo/${testId}/questions/${qid}/image`);
      setDraft((d) => ({
        ...d,
        questions: d.questions.map((q) => (q.id === qid ? { ...q, image_path: null } : q)),
      }));
      toast.success("Imagem removida.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao remover.");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4" onClick={onClose} data-testid="test-editor-modal">
      <div className="bg-card border border-border rounded-sm p-6 max-w-3xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="overline text-gold mb-1">{isNew ? "Novo teste" : "Editar teste"}</div>
            <h3 className="brand-wordmark uppercase text-lg font-bold text-primary">Builder de Teste</h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded-sm"><X className="w-4 h-4" /></button>
        </div>

        {/* Info */}
        <div className="space-y-3 mb-5">
          <div>
            <label className="overline text-muted-foreground mb-1 block">Nome</label>
            <input
              value={draft.name}
              onChange={(e) => setField("name", e.target.value)}
              className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
              placeholder="Ex.: Risco Cardiometabólico"
              data-testid="test-name-input"
            />
          </div>
          <div>
            <label className="overline text-muted-foreground mb-1 block">Descrição</label>
            <textarea
              rows={2}
              value={draft.description}
              onChange={(e) => setField("description", e.target.value)}
              className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
              placeholder="Ex.: Avaliação rápida de fatores de risco cardiovascular…"
              data-testid="test-desc-input"
            />
          </div>
          {!isNew && (
            <div>
              <label className="overline text-muted-foreground mb-1 block">
                Caminho da URL (slug)
              </label>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground shrink-0">/testes/</span>
                <input
                  value={draft.slug}
                  onChange={(e) => setField("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]+/g, "-"))}
                  className="flex-1 px-3 py-2 rounded-sm border border-input bg-card text-sm font-mono"
                  placeholder="avaliacao-intestinal"
                  data-testid="test-slug-input"
                />
              </div>
              <p className="text-[10px] text-muted-foreground mt-1 italic">
                Só letras minúsculas, números e hífens. Renomeie sem perder o teste — o slug fica único automaticamente.
              </p>
            </div>
          )}
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={draft.active}
              onChange={(e) => setField("active", e.target.checked)}
              data-testid="test-active-toggle"
            />
            Ativo (visível na página pública /testes)
          </label>
        </div>

        {/* Parecer final / Considerações do Especialista */}
        <div className="mb-5">
          <label className="overline text-muted-foreground mb-1 block">
            Texto de Conclusão e Orientações Gerais
          </label>
          <textarea
            rows={5}
            value={draft.conclusion_text}
            onChange={(e) => setField("conclusion_text", e.target.value)}
            className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm font-mono leading-relaxed"
            placeholder={`Exibido no relatório final como "Considerações do Especialista".

Aceita quebras de linha e links (https://...).

Ex.:
Estas hipóteses precisam de confirmação clínica. Agende sua consulta.
WhatsApp: https://wa.me/5511999998888
Instagram: https://instagram.com/seuinstagram
Aviso: relatório orientativo — não substitui consulta presencial.`}
            data-testid="test-conclusion-input"
          />
          <p className="text-[10px] text-muted-foreground mt-1 italic">
            Deixe em branco para omitir a seção do relatório. Quebras de linha e URLs são preservadas
            no web e no PDF.
          </p>
        </div>

        {/* Questions */}
        <div className="mb-5">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold text-primary uppercase tracking-wider">Perguntas</h4>
            <button onClick={addQ} className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-sm border border-border hover:bg-muted" data-testid="add-question-btn">
              <Plus className="w-3 h-3" /> Nova pergunta
            </button>
          </div>
          <div className="space-y-2">
            {draft.questions.map((q, idx) => (
              <div key={idx} className="bg-muted/40 border border-border rounded-sm p-3" data-testid={`question-row-${idx}`}>
                <div className="flex items-start gap-2">
                  <div className="flex flex-col text-muted-foreground pt-1.5">
                    <button onClick={() => moveQ(idx, -1)} disabled={idx === 0} className="p-0.5 disabled:opacity-30" title="Subir">↑</button>
                    <button onClick={() => moveQ(idx, 1)} disabled={idx === draft.questions.length - 1} className="p-0.5 disabled:opacity-30" title="Descer">↓</button>
                  </div>
                  <div className="flex-1 space-y-2">
                    <input
                      value={q.text}
                      onChange={(e) => setQ(idx, "text", e.target.value)}
                      placeholder={`Pergunta ${idx + 1}`}
                      className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
                      data-testid={`q-text-${idx}`}
                    />
                    <div className="flex flex-wrap items-center gap-2">
                      <select
                        value={q.type}
                        onChange={(e) => setQ(idx, "type", e.target.value)}
                        className="px-2 py-1.5 rounded-sm border border-input bg-card text-xs"
                        data-testid={`q-type-${idx}`}
                      >
                        {QUESTION_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                      <label className="text-xs text-muted-foreground">Peso:</label>
                      <input
                        type="number" step="0.5"
                        value={q.weight}
                        onChange={(e) => setQ(idx, "weight", e.target.value)}
                        className="w-20 px-2 py-1.5 rounded-sm border border-input bg-card text-xs"
                        data-testid={`q-weight-${idx}`}
                      />
                      <span className="text-[11px] text-muted-foreground italic">
                        {q.type === "yes_no"
                          ? `Soma ${q.weight || 0} se resposta = Sim`
                          : `Soma ${q.weight || 0} × valor (1–5)`}
                      </span>
                    </div>
                    {/* Reference image: only allowed on already-saved questions (needs q.id) */}
                    <div className="pt-2 border-t border-border/50">
                      {q.image_path ? (
                        <div className="flex items-start gap-3">
                          <img
                            src={imageUrl(q.image_path)}
                            alt="Imagem de referência"
                            className="w-28 h-28 object-cover rounded-sm border border-border"
                            data-testid={`q-image-preview-${idx}`}
                          />
                          <div className="flex flex-col gap-1 text-xs">
                            <div className="text-muted-foreground flex items-center gap-1">
                              <ImageIcon className="w-3 h-3" /> Imagem de referência anexada
                            </div>
                            <button
                              type="button"
                              onClick={() => removeImage(q.id)}
                              className="self-start text-destructive hover:underline"
                              data-testid={`q-image-remove-${idx}`}
                            >
                              Remover imagem
                            </button>
                          </div>
                        </div>
                      ) : (
                        <label className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-sm border ${
                          testId && q.id
                            ? "border-primary text-primary hover:bg-primary/5 cursor-pointer"
                            : "border-border text-muted-foreground cursor-not-allowed"
                        }`}
                          data-testid={`q-image-upload-label-${idx}`}
                          title={!testId ? "Salve o teste primeiro para poder anexar imagens" : ""}
                        >
                          <ImagePlus className="w-3.5 h-3.5" />
                          {uploadingQid === q.id ? "Enviando…" : "Anexar imagem"}
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/webp,image/gif"
                            className="hidden"
                            disabled={!testId || !q.id || uploadingQid === q.id}
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              e.target.value = "";
                              if (f) uploadImage(q.id, f);
                            }}
                            data-testid={`q-image-input-${idx}`}
                          />
                        </label>
                      )}
                      {(!testId || !q.id) && !q.image_path && (
                        <p className="text-[10px] text-muted-foreground mt-1 italic">
                          Salve o teste primeiro para anexar imagens de referência a cada pergunta.
                        </p>
                      )}
                    </div>
                  </div>
                  <button onClick={() => removeQ(idx)} className="p-1 rounded-sm text-destructive hover:bg-destructive/10" data-testid={`q-delete-${idx}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Score bands */}
        <div className="mb-5">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold text-primary uppercase tracking-wider">Faixas de score</h4>
            <button onClick={addBand} className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-sm border border-border hover:bg-muted" data-testid="add-band-btn">
              <Plus className="w-3 h-3" /> Nova faixa
            </button>
          </div>
          <div className="space-y-2">
            {draft.score_bands.map((b, idx) => (
              <div key={idx} className="bg-muted/40 border border-border rounded-sm p-3 flex flex-wrap items-center gap-2" data-testid={`band-row-${idx}`}>
                <div className="flex items-center gap-1 text-xs">
                  <span>Min</span>
                  <input type="number" value={b.min} onChange={(e) => setBand(idx, "min", e.target.value)}
                    className="w-20 px-2 py-1.5 rounded-sm border border-input bg-card text-xs" data-testid={`band-min-${idx}`} />
                </div>
                <div className="flex items-center gap-1 text-xs">
                  <span>Max</span>
                  <input type="number" value={b.max} onChange={(e) => setBand(idx, "max", e.target.value)}
                    className="w-20 px-2 py-1.5 rounded-sm border border-input bg-card text-xs" data-testid={`band-max-${idx}`} />
                </div>
                <select value={b.color} onChange={(e) => setBand(idx, "color", e.target.value)}
                  className="px-2 py-1.5 rounded-sm border border-input bg-card text-xs" data-testid={`band-color-${idx}`}>
                  {BAND_COLORS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
                <span className={`w-3 h-3 rounded-full ${BAND_COLORS.find((c) => c.value === b.color)?.cls || "bg-muted"}`} />
                <input
                  value={b.label}
                  onChange={(e) => setBand(idx, "label", e.target.value)}
                  placeholder="Rótulo (ex: Baixo risco)"
                  className="flex-1 min-w-[140px] px-2 py-1.5 rounded-sm border border-input bg-card text-xs"
                  data-testid={`band-label-${idx}`}
                />
                <button onClick={() => removeBand(idx)} className="p-1 rounded-sm text-destructive hover:bg-destructive/10" data-testid={`band-delete-${idx}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t border-border">
          <button onClick={onClose} className="px-4 py-2 rounded-sm border border-border text-sm hover:bg-muted">Cancelar</button>
          <button onClick={save} disabled={saving}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-sm bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 disabled:opacity-60"
            data-testid="save-test-btn">
            <Save className="w-4 h-4" /> {saving ? "Salvando…" : "Salvar teste"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminTestsCatalogPage() {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} (new) | test object
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/testes-catalogo");
      setTests(data || []);
    } catch (_) {/* noop */}
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const remove = async (t) => {
    if (!window.confirm(`Excluir o teste "${t.name}"? Submissões existentes serão mantidas mas o teste sairá da /testes.`)) return;
    try {
      await api.delete(`/admin/testes-catalogo/${t.id}`);
      toast.success("Teste excluído.");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao excluir."); }
  };

  const toggleActive = async (t) => {
    try {
      await api.patch(`/admin/testes-catalogo/${t.id}`, { active: !t.active });
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro."); }
  };

  const copyPublicLink = async (t) => {
    const link = `${window.location.origin}/testes/${t.slug}`;
    try { await navigator.clipboard.writeText(link); toast.success("Link público copiado."); }
    catch (_) { toast.error("Copie manualmente: " + link); }
  };

  return (
    <div className="space-y-6" data-testid="admin-tests-catalog-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-gold mb-3 inline-flex items-center gap-2"><Library className="w-3.5 h-3.5" /> Biblioteca</div>
          <h1 className="brand-wordmark text-3xl md:text-4xl font-bold tracking-tight uppercase text-primary">Biblioteca de Testes</h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">
            Crie e publique testes públicos em <code>/testes</code>. Cada teste tem perguntas ponderadas e faixas de score coloridas.
          </p>
        </div>
        <button onClick={() => { setEditing(null); setCreating(true); }}
          className="inline-flex items-center gap-2 rounded-sm bg-primary text-primary-foreground px-4 py-2.5 text-sm font-semibold hover:bg-primary/90 transition-colors"
          data-testid="new-test-btn">
          <Plus className="w-4 h-4" /> Novo teste
        </button>
      </div>

      {loading ? (
        <div className="py-12 text-center text-sm text-muted-foreground overline">Carregando…</div>
      ) : tests.length === 0 ? (
        <div className="py-12 text-center text-sm text-muted-foreground bg-card border border-dashed border-border rounded-sm">
          Nenhum teste criado ainda. Clique em <span className="font-semibold">&quot;Novo teste&quot;</span> para publicar seu primeiro.
        </div>
      ) : (
        <div className="bg-card border border-border rounded-sm overflow-hidden">
          <div className="divide-y divide-border">
            {tests.map((t) => (
              <div key={t.id} className="p-4 hover:bg-muted/40 transition-colors" data-testid={`test-row-${t.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center flex-wrap gap-2">
                      <div className="font-heading font-semibold text-primary">{t.name}</div>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border text-[10px] font-semibold uppercase tracking-wider ${
                        t.active
                          ? "bg-emerald-500/15 text-emerald-700 border-emerald-500/30"
                          : "bg-muted text-muted-foreground border-border"
                      }`}>
                        {t.active ? "Ativo" : "Inativo"}
                      </span>
                      <code className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded-sm">/{t.slug}</code>
                    </div>
                    {t.description && <div className="text-xs text-muted-foreground mt-1 leading-relaxed">{t.description}</div>}
                    <div className="text-xs text-muted-foreground mt-1">
                      {(t.questions?.length || 0)} pergunta{(t.questions?.length || 0) !== 1 ? "s" : ""} ·
                      {" "}{(t.score_bands?.length || 0)} faixa{(t.score_bands?.length || 0) !== 1 ? "s" : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-wrap">
                    <button onClick={() => toggleActive(t)}
                      className="p-1.5 rounded-sm border border-border hover:bg-muted transition-colors inline-flex items-center gap-1 text-xs px-2"
                      data-testid={`test-toggle-${t.id}`}>
                      {t.active ? <><EyeOff className="w-3.5 h-3.5" /> Desativar</> : <><Eye className="w-3.5 h-3.5" /> Ativar</>}
                    </button>
                    <button onClick={() => copyPublicLink(t)}
                      className="p-1.5 rounded-sm border border-border hover:bg-muted transition-colors inline-flex items-center gap-1 text-xs px-2"
                      data-testid={`test-copy-${t.id}`}>
                      <Copy className="w-3.5 h-3.5" /> Link
                    </button>
                    <a href={`/testes/${t.slug}`} target="_blank" rel="noopener noreferrer"
                      className="p-1.5 rounded-sm border border-border hover:bg-muted transition-colors inline-flex items-center gap-1 text-xs px-2"
                      data-testid={`test-open-${t.id}`}>
                      <ExternalLink className="w-3.5 h-3.5" /> Abrir
                    </a>
                    <button onClick={() => { setEditing(t); setCreating(false); }}
                      className="p-1.5 rounded-sm border border-primary text-primary hover:bg-primary/5 transition-colors text-xs px-2"
                      data-testid={`test-edit-${t.id}`}>
                      Editar
                    </button>
                    <button onClick={() => remove(t)}
                      className="p-1.5 rounded-sm border border-destructive/30 text-destructive hover:bg-destructive/5 transition-colors"
                      data-testid={`test-delete-${t.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {(creating || editing) && (
        <EditorModal
          test={editing}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={() => load()}
        />
      )}
    </div>
  );
}
